import { Cancel01Icon, CloudUploadIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import * as React from 'react';

import { Button } from './button';
import { cn } from './lib/utils';

export interface FileSelectionProps {
  /** The currently-selected files (controlled by the parent). */
  files: File[];
  /** Called whenever the selection changes (add via picker/drop, or remove). */
  onFilesChange: (files: File[]) => void;
  /** `accept` forwarded to the underlying file input, e.g. `'image/*'`. */
  accept?: string;
  /** Allow selecting more than one file. New selections are appended. */
  multiple?: boolean;
  disabled?: boolean;
  /** `id` for the underlying input, so an external `<label htmlFor>` can target it. */
  id?: string;
  className?: string;
}

const isImage = (file: File) => file.type.startsWith('image/');
const isVideo = (file: File) => file.type.startsWith('video/');

/**
 * A file picker with a drag-and-drop zone and inline, client-side previews.
 *
 * Previews are produced with `URL.createObjectURL` — nothing is uploaded here;
 * the parent decides when to send the `files` to the server. Object URLs are
 * revoked when the selection changes or the component unmounts.
 */
function FileSelection({
  files,
  onFilesChange,
  accept,
  multiple = true,
  disabled = false,
  id,
  className,
}: FileSelectionProps) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = React.useState(false);

  // Object URLs derived from the current files; revoked on change/unmount.
  const [previews, setPreviews] = React.useState<(string | null)[]>([]);
  React.useEffect(() => {
    const urls = files.map((file) =>
      isImage(file) || isVideo(file) ? URL.createObjectURL(file) : null,
    );
    setPreviews(urls);
    return () => {
      for (const url of urls) {
        if (url) URL.revokeObjectURL(url);
      }
    };
  }, [files]);

  const addFiles = (incoming: FileList | null) => {
    if (!incoming || incoming.length === 0) return;
    const selected = Array.from(incoming);
    onFilesChange(multiple ? [...files, ...selected] : selected.slice(0, 1));
  };

  const removeAt = (index: number) => {
    onFilesChange(files.filter((_, i) => i !== index));
  };

  const openPicker = () => {
    if (!disabled) inputRef.current?.click();
  };

  return (
    <div data-slot="file-selection" className={cn('space-y-2', className)}>
      <button
        type="button"
        disabled={disabled}
        onClick={openPicker}
        onDragOver={(event) => {
          event.preventDefault();
          if (!disabled) setDragActive(true);
        }}
        onDragLeave={(event) => {
          event.preventDefault();
          setDragActive(false);
        }}
        onDrop={(event) => {
          event.preventDefault();
          setDragActive(false);
          if (!disabled) addFiles(event.dataTransfer.files);
        }}
        className={cn(
          'flex w-full flex-col items-center justify-center gap-1.5 rounded-lg border border-dashed px-4 py-6 text-center transition-colors outline-none',
          'focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50',
          disabled ? 'cursor-not-allowed opacity-50' : 'hover:bg-muted/50',
          dragActive ? 'border-ring bg-muted/50' : 'border-input',
        )}
      >
        <HugeiconsIcon
          icon={CloudUploadIcon}
          className="text-muted-foreground size-6"
          aria-hidden={true}
        />
        <span className="text-sm">
          <span className="font-medium">Click to upload</span>
          <span className="text-muted-foreground"> or drag and drop</span>
        </span>
      </button>

      <input
        ref={inputRef}
        id={id}
        type="file"
        accept={accept}
        multiple={multiple}
        disabled={disabled}
        className="sr-only"
        onChange={(event) => {
          addFiles(event.target.files);
          // Reset so selecting the same file again still fires `change`.
          event.target.value = '';
        }}
      />

      {files.length > 0 ? (
        <ul className="grid grid-cols-[repeat(auto-fill,minmax(5rem,1fr))] gap-2">
          {files.map((file, index) => {
            const preview = previews[index];
            return (
              <li
                key={`${file.name}-${String(file.size)}-${String(file.lastModified)}`}
                className="relative"
              >
                <div className="bg-muted flex aspect-square items-center justify-center overflow-hidden rounded-md border">
                  {preview && isImage(file) ? (
                    <img src={preview} alt={file.name} className="h-full w-full object-cover" />
                  ) : preview && isVideo(file) ? (
                    <video src={preview} muted className="h-full w-full object-cover">
                      <track kind="captions" />
                    </video>
                  ) : (
                    <span className="text-muted-foreground truncate px-1 text-[10px]">
                      {file.name}
                    </span>
                  )}
                </div>
                <Button
                  type="button"
                  variant="secondary"
                  size="icon-xs"
                  disabled={disabled}
                  onClick={() => {
                    removeAt(index);
                  }}
                  aria-label={`Remove ${file.name}`}
                  className="absolute -end-1.5 -top-1.5 rounded-full border shadow-sm"
                >
                  <HugeiconsIcon icon={Cancel01Icon} strokeWidth={2} />
                </Button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}

export { FileSelection };
